/**
 * 家长批改 —— `/students/:id/dictation/:sessionId/review`。
 *
 * ## 这一页原来是"只读回看"（第三步），现在它是**批改台**
 *
 * 用户原话："写完之后，家长就去批改。批改之后，把没有听写出来或者错的词，
 * 在系统里面登记，为下一次再一次去听写这些错误的词做好登记。"
 *
 * 所以这一页现在的形状完全围绕**那个动作**设计：
 *
 *   1. **默认全对**：一进来每题都是「对」，家长只需要点出错的那几题 ——
 *      错的是少数，绝不能让他每题点两次（这是这一页存在的全部理由）。
 *      界面上**明说**这条默认（"没点的都算对"），否则家长会把 20 题都点一遍。
 *   2. **三种结果**：对 / **写错了** / **没写出来**。后两者是两件事
 *      （用户原话"没有听写出来或者错的词"），也会分别记进错词本的计数。
 *   3. 每题显示 **词面 + 拼音 + 这个词的历史错误次数** ——
 *      家长看到"以前写错 3 次"就知道该盯住它，而不是随手点个"错"。
 *   4. 提交 → 会话变「已批改」→ 错词进错词本并按阶梯排下次复习。
 *
 * ## 可重复修改（点错了能改）
 *
 * 批改是**可重复提交**的：再点一次「保存批改」就按最新的判定重算。
 * 重复提交**不会**重复累加错词次数（幂等由后端的 `counted_mark` 保证），
 * 界面上还给出直接证据：返回里的 `changedQuestions = 0` 会被显示成
 * "没有改动（N 道题的判定和上次一样）"。
 *
 * ## 手机优先
 *
 * 真实场景是家长拿着手机、孩子在旁边。所以：
 *   · 每一题一行，**一屏能看 4~6 题**；
 *   · 三个判定做成一行**大按钮**（最小 44px 高，拇指够得着）；
 *   · 底部一个**固定的**「保存批改」按钮，滚到哪都能点；
 *   · 窄屏用 `min-w-0 flex-1` + `shrink-0`，不出现横向滚动。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/Bits';
import { useToast } from '../../components/ui/Toast';
import { IconAlertTriangle, IconCheck, IconChevronLeft, IconHistory, IconMic, IconVolume2 } from '../../components/ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import { countLabel } from '../../lib/api/dictation';
import * as sessionApi from '../../lib/api/dictationSessions';
import type { DictationMarkView, DictationQuestionView } from '../../lib/api/dictationSessions';
import { formatDateTimeLocal } from '../../lib/utils';
import { describeSessionOrigin } from '../../lib/dictationBuild';
import { stripBatchSuffix } from '../../lib/dictationHistory';
import {
  MARK_FULL_LABEL,
  MARK_LABEL,
  buildGradeMarks,
  countProblems,
  describeGradeOutcome,
  describeQuestionHistory,
  initialMarks,
} from '../../lib/dictationGrading';

/** `pending` / `played` / `skipped` → 人话。认不出来的原样显示。 */
export function questionStatusLabel(status: string): string {
  if (status === 'pending') return '还没听';
  if (status === 'played') return '读过了';
  if (status === 'skipped') return '跳过了';
  return status === '' ? '—' : status;
}

/** 三选一的按钮样式（**选中态必须一眼看出来**：家长会来回改）。 */
function markButtonClass(mark: DictationMarkView, active: boolean): string {
  const base =
    'flex-1 shrink-0 basis-0 rounded-lg border px-2 py-2.5 text-sm font-medium transition-colors min-w-0';
  if (!active) return `${base} border-slate-300 bg-white text-slate-600 hover:bg-slate-50`;
  if (mark === 'correct') return `${base} border-emerald-500 bg-emerald-50 text-emerald-700`;
  if (mark === 'wrong') return `${base} border-amber-500 bg-amber-50 text-amber-800`;
  return `${base} border-rose-500 bg-rose-50 text-rose-700`;
}

export function ParentDictationReviewPage() {
  const { id = '', sessionId = '' } = useParams<{ id: string; sessionId: string }>();
  const toast = useToast();
  const qc = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ['dictation-sessions', id, 'detail', sessionId] as const,
    queryFn: () => sessionApi.getSession(id, sessionId),
    enabled: id !== '' && sessionId !== '',
  });

  /**
   * 家长的改动（题 id → 判定）。
   *
   * `null` = 还没动过 → 用 `initialMarks()` 算出的初值（库里的判定，或默认全对）。
   * 一旦动过就是一份完整表 —— 这样"服务端刷新"不会把家长正在改的东西清掉。
   */
  const [edited, setEdited] = useState<Record<string, DictationMarkView> | null>(null);
  const [gradeNotice, setGradeNotice] = useState<string | null>(null);

  const questions = sessionQuery.data?.questions ?? null;

  /** 当前生效的判定表（家长改过的优先，否则是初值）。**必须记忆化**：它是提交的输入。 */
  const marks = useMemo(() => {
    if (edited !== null) return edited;
    return initialMarks(questions ?? []);
  }, [edited, questions]);

  const summary = useMemo(() => countProblems(marks), [marks]);

  const gradeMutation = useMutation({
    mutationFn: () =>
      sessionApi.gradeSession(id, sessionId, buildGradeMarks(questions ?? [], marks)),
    onSuccess: (result) => {
      setGradeNotice(describeGradeOutcome(result));
      toast.showToast('批改已保存', 'success');
      /** 判定已落库 → 丢掉本地改动，让界面回到"服务端说了算"。 */
      setEdited(null);
      void qc.invalidateQueries({ queryKey: ['dictation-sessions', id] });
      void qc.invalidateQueries({ queryKey: ['dictation-mistakes', id] });
    },
    onError: (error: unknown) => {
      setGradeNotice(null);
      toast.showToast(`批改没保存上：${toErrorMessage(error)}`, 'error');
    },
  });

  if (sessionQuery.isLoading) {
    return (
      <AppShell kind="parent" title="批改这次听写">
        <div className="space-y-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (sessionQuery.isError || sessionQuery.data === undefined) {
    return (
      <AppShell kind="parent" title="批改这次听写">
        <ErrorState
          message={`没拿到这次听写：${toErrorMessage(sessionQuery.error)}`}
          onRetry={() => void sessionQuery.refetch()}
        />
      </AppShell>
    );
  }

  const session = sessionQuery.data;
  /**
   * 能不能批改。
   *
   * `pending` / `in_progress` = 孩子还没写完 —— 这时候批改会把"还没做的部分"
   * 当成错词登记进去，冤枉孩子。后端也会拒（`DICTATION_NOT_GRADEABLE`），
   * 但界面**不能等到提交才说**：那是一次白填的输出。
   */
  const gradeable = session.status === 'completed' || session.status === 'graded';
  const historyHref = `/students/${id}/dictation/mistakes`;
  /**
   * **回听写历史**（2026-10-29 用户要求）。
   *
   * > "你点进去看题目或者批改，好像没有返回到「听写历史」的按钮。你加一个返回的
   * >  按钮吧……不然直接点浏览器的返回，或者点左上角那个箭头，就会回到「开始听写」
   * >  那里了。"
   *
   * ⚠️ 为什么不是把左上角那支箭头改掉：箭头由 `lib/backNav.ts` 按路由推导，
   *    "这次听写"（播放器）是回看页的**上一级**（预习卡也回它），而回看页与
   *    听写列表**段数相同**（都是 5 段），改了会让返回链出现同层跳转、
   *    破坏 `backNav.test.ts` 里"层级严格递减、不成环"那条守卫。
   *    所以这里给一个**明确的按钮**：从历史进来的家长一眼能看到回去的路，
   *    而"看题目 → 看播放器 → 回列表"那条链一个字都不用动。
   *
   * `?tab=history` 让列表页直接落在「听写历史」页签，而不是默认的「批量任务」。
   */
  const backToHistoryHref = `/students/${id}/dictation?tab=history`;

  return (
    <AppShell kind="parent" title="批改这次听写">
      {/* 手机上下边距要留出「保存批改」横条 + 底部导航两层（见下面那条 fixed 的注释） */}
      <div className="space-y-4 pb-[calc(9rem+env(safe-area-inset-bottom))] md:pb-24">
        {/* ---------- 回听写历史（最上面，先看到回去的路） ---------- */}
        <div className="flex flex-wrap items-center gap-2">
          <Link to={backToHistoryHref}>
            <Button size="sm" variant="secondary" icon={<IconChevronLeft size={16} />}>
              回听写历史
            </Button>
          </Link>
        </div>

        <Card>
          {/*
            ⚠️ 2026-10-29 用户连提三次，这一张卡片最后收敛成**三行**：
              1) 标题原来 `truncate` 会被截成「人民教育出版社 · …」，而"第几批"正好在**末尾**；
              2) 改成换行后，标题与三个标签同一行，标签把标题挤成每行四五个字；
              3) 现在：**第 1 行标题、第 2 行标签**，并且**删掉**了
                 「全部年级 · 全部学期 · 按文字顺序 · 每个词读 2 遍…」与
                 「布置于 / 开始于 / 读完于」两行 —— 用户原话："那点什么全部年级、
                 全部学期，这些不要，还有那三个时间……这三个时间不要，删掉"。
                 批改时家长要的是"哪个任务的第几批 + 每题对错"，其余都是噪点。
          */}
          <div className="min-w-0">
            <h2 className="break-words text-lg font-bold text-slate-900">
              {stripBatchSuffix(session.title, session.batchNo)}
            </h2>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {/* 批次：批量任务的一批才有（独立听写是 null，不显示这个标签） */}
            {session.batchNo !== null && <Badge tone="brand">第 {session.batchNo} 批</Badge>}
            <Badge tone={session.status === 'graded' ? 'success' : session.status === 'completed' ? 'brand' : 'warn'}>
              {sessionApi.sessionStatusLabel(session.status)}
            </Badge>
            <Badge tone={questions === null ? 'warn' : 'neutral'}>
              共 {questions === null ? '—' : countLabel(questions.length)} 个词
            </Badge>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="批改"
            description="默认都是「对」，只需要点出错的那几题。「写错了」和「没写出来」会分开记，下次都会重听。"
            actionAlign="start"
            action={
              <div className="flex basis-full flex-wrap items-center gap-2">
                <Link to={historyHref}>
                  <Button size="sm" variant="secondary">
                    <span className="mr-1 inline-flex">
                      <IconHistory size={14} />
                    </span>
                    错词本
                  </Button>
                </Link>
                <Link to={`/students/${id}/dictation/${sessionId}`}>
                  <Button size="sm" variant="ghost">
                    去播放器
                  </Button>
                </Link>
              </div>
            }
          />

          {!gradeable && (
            <p className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <span className="mt-0.5 shrink-0">
                <IconAlertTriangle size={16} />
              </span>
              <span>
                这次听写**孩子还没做完**（现在是「{sessionApi.sessionStatusLabel(session.status)}」），
                先让孩子在播放器里听完、把状态标成「已完成」，再回来批改。
              </span>
            </p>
          )}

          {questions === null ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              没拿到这次的题目（后端返回的字段名与预期不一致）—— 这不是"这次听写没有题目"。
              <Button
                size="sm"
                variant="secondary"
                className="ml-2"
                onClick={() => void sessionQuery.refetch()}
              >
                重新取一次
              </Button>
            </p>
          ) : questions.length === 0 ? (
            <EmptyState icon="📝" title="这次听写没有题目" description="请重新布置一次。" />
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="neutral">共 {questions.length} 题</Badge>
                <Badge tone={summary.wrong > 0 ? 'warn' : 'neutral'}>写错 {summary.wrong}</Badge>
                <Badge tone={summary.missing > 0 ? 'warn' : 'neutral'}>
                  没写出来 {summary.missing}
                </Badge>
                <span className="text-xs text-slate-500">没点的都算「对」</span>
              </div>

              <ul className="space-y-2">
                {questions.map((question) => (
                  <QuestionRow
                    key={question.id}
                    question={question}
                    mark={marks[question.id] ?? 'correct'}
                    disabled={!gradeable || gradeMutation.isPending}
                    onChange={(next) =>
                      setEdited({ ...marks, [question.id]: next })
                    }
                  />
                ))}
              </ul>
            </>
          )}
        </Card>

        {gradeNotice !== null && (
          <Card className="bg-emerald-50">
            <p className="text-sm text-emerald-800">{gradeNotice}</p>
          </Card>
        )}

        {questions !== null && questions.length > 0 && (
          <Card className="bg-slate-50">
            <p className="text-xs text-slate-500">
              批改可以**反复改**：点错了就再点一次，重新保存即可。
              同一个词重复保存不会把错误次数重复累加 —— 错词本记的是这个词一共错过几次。
            </p>
          </Card>
        )}
      </div>

      {/*
        底部固定的「保存批改」：孩子坐在旁边时家长是**单手**操作的，
        让按钮跟着滚动条跑会让他滑半天找不到。

        ⚠️ 2026-10-29 修（用户手机截图报的"批改完没有保存按钮"）：
        这一条原来写的是 `bottom-0`，而 `AppShell` 的手机底部导航
        （孩子 / 工具 / 奖品 / 设置）也是 `fixed bottom-0`，且 `z-40` 更高 ——
        于是**保存按钮被底部导航整条盖住了**（桌面上没有底部导航，所以只有手机上看不见）。
        现在手机上把它抬到导航之上（`bottom-[calc(4.5rem+安全区)]`），桌面仍贴底。
        页面内容的下边距也要跟着抬，否则最后一题会被这条横条压住。
      */}
      {questions !== null && questions.length > 0 && (
        <div className="fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:bottom-0">
          <div className="mx-auto flex max-w-3xl items-center gap-3">
            <div className="min-w-0 flex-1 text-xs text-slate-600">
              写错 {summary.wrong} · 没写出来 {summary.missing} · 其余 {questions.length - summary.wrong - summary.missing} 题算对
            </div>
            <Button
              className="shrink-0"
              disabled={!gradeable}
              loading={gradeMutation.isPending}
              onClick={() => {
                setGradeNotice(null);
                gradeMutation.mutate();
              }}
            >
              保存批改
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

/** 一题：上面词面/拼音 + 状态标签，下面三个判定按钮（手机上上下叠）。 */
function QuestionRow({
  question,
  mark,
  disabled,
  onChange,
}: {
  question: DictationQuestionView;
  mark: DictationMarkView;
  disabled: boolean;
  onChange: (next: DictationMarkView) => void;
}) {
  return (
    <li className="rounded-xl border border-slate-200 px-3 py-2.5">
      {/*
        ⚠️ 2026-10-29 修：**词面被挤成一条竖线**（用户在手机上截图报的）。
        原来词面与右侧那一串标签是**同一行**的左右两半：词面 `min-w-0 flex-1`
        会被压到接近 0 宽（`truncate` 于是只剩一两个像素），而右侧
        「第一次听写这个词 / 浏览器语音 / 读过了」三个标签是 `shrink-0`，不让位。
        现在改成**上下两层**：词面独占一行（`w-full`），标签换到下一行。
        手机上家长看的是"这是哪个词"，任何情况下都不该被标签挤没。
      */}
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="shrink-0 text-xs text-slate-400">{question.position}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-slate-900">{question.wordText}</span>
          <span className="block font-mono text-xs text-slate-500">
            {question.pinyin === '' ? '（没有读音）' : question.pinyin}
          </span>
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {/*
          历史错误次数：**拿不到时说「—」而不是"没错过"**（见 describeQuestionHistory）。
          这是家长判断"要不要盯住这个词"的依据，所以它必须看得见。
        */}
        <Badge tone={question.mistake == null ? 'neutral' : 'warn'}>
          {describeQuestionHistory(question)}
        </Badge>
        {question.hasAudio === null ? (
          <Badge tone="warn">朗读来源没拿到</Badge>
        ) : question.spokenSource === 'parent_audio' ? (
          <Badge tone="success">
            <span className="mr-1 inline-flex">
              <IconMic size={12} />
            </span>
            家长录音
          </Badge>
        ) : (
          <Badge tone="neutral">
            <span className="mr-1 inline-flex">
              <IconVolume2 size={12} />
            </span>
            浏览器语音
          </Badge>
        )}
        <Badge tone={question.status === 'skipped' ? 'warn' : 'neutral'}>
          {questionStatusLabel(question.status)}
        </Badge>
      </div>

      {/*
        三个判定：**按钮足够大**（py-2.5 + text-sm ≈ 44px 高），一行放得下。
        选中的那个有明确的边框+底色（家长会来回改，选中态必须一眼看出来）。
      */}
      <div className="mt-2 flex gap-2" role="group" aria-label={`第 ${question.position} 题：${question.wordText} 的批改结果`}>
        {(Object.keys(MARK_LABEL) as DictationMarkView[]).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={mark === option}
            title={MARK_FULL_LABEL[option]}
            disabled={disabled}
            onClick={() => onChange(option)}
            className={markButtonClass(option, mark === option)}
          >
            {MARK_LABEL[option]}
            {mark === option && (
              <span className="ml-1 inline-flex align-middle">
                <IconCheck size={14} />
              </span>
            )}
          </button>
        ))}
      </div>
    </li>
  );
}
